import type {
	CampaignImpression,
	SubscriptionDetailsResponse,
	SubscriptionPreviewUpgradeResponse,
	SubscriptionResponse,
} from '@kimuno/api-sdk';
import config from 'config';
import { createSelector } from 'reselect';

import { licenceCategory, premiumUserTypes, userType } from '../constants';
import type { User, WithdrawalRequestStatus } from '../types/User';
import { flattenUserSubjects } from '../utils';
import type { StoreType } from '.';

const getMe = (state: StoreType) => state.user.me;

// Stable empty reference so the selector doesn't return a fresh array on every
// call for users who have triggered no hints (avoids needless re-renders).
const EMPTY_TRIGGERED_HINTS: string[] = [];

export const selectTriggeredHints = (state: StoreType): string[] =>
	state.user.me?.data?.triggeredHints ?? EMPTY_TRIGGERED_HINTS;

// Same stable-reference trick as EMPTY_TRIGGERED_HINTS, and the safe default
// while the APIv3 field is not yet deployed (missing field = zero impressions).
const EMPTY_CAMPAIGN_IMPRESSIONS: Record<string, CampaignImpression> = {};

export const selectCampaignImpressions = (state: StoreType): Record<string, CampaignImpression> =>
	state.user.me?.data?.campaignImpressions ?? EMPTY_CAMPAIGN_IMPRESSIONS;

const getInvoiceHistory = (state: StoreType) => state.user.invoiceHistory;
const getSchoolInvoiceHistory = (state: StoreType) => state.user.schoolInvoiceHistory;

export const getSubscription = (state: StoreType) => state.user.subscription;
export const getSubscriptionUpgradePreview = (state: StoreType) =>
	state.user.subscriptionUpgradePreview;

const getPaymentMethod = (state: StoreType) => state.user.paymentMethod;

// SM-92 Phase 6 — Stripe-side subscription details (status / trialEndAt /
// cancelAt). `undefined` until the fetch resolves; `null` on the inner
// `subscription` field when the user has no Stripe sub.
export const getStripeSubscriptionDetails = (state: StoreType) =>
	state.user.stripeSubscriptionDetails;

// SM-117: single source of truth for "is this subscription Stripe-backed" — the
// discriminator that routes withdrawal (Widerruf) eligibility, intake, and the
// "being processed" state between the kimuno revocation flow and the legacy
// Zuora path. Shared by selectUser, ManageSubscriptionDialog, and
// WithdrawSubDialog so the three can't drift.
export const isStripeBackedSubscription = (
	details: SubscriptionDetailsResponse | undefined
): boolean => !!details?.subscription;

// SM-411: whether the current user has an in-flight pause request (any non-
// terminal status). Derived from the `pause` block on the subscription-details
// response — the block is only ever present for open rows (server filters
// `resumed` / `rejected` out), so a truthy `pause` is the signal. Shared by
// `mapStripeSubscriptionStatus` and the pause-tile visibility rule so the two
// can't drift on which rows count as "open".
export const hasOpenPauseRequest = (details: SubscriptionDetailsResponse | undefined): boolean =>
	!!details?.pause;

export const getExperiments = (state: StoreType) => state.user.experiments;

export const getSubscriptionStatus = (state: StoreType) => state.user.subscriptionStatus;

export const getMeStatus = (state: StoreType) => state.user.status;

export const selectUserInvoices = (state: StoreType) => state.user.userInvoices;
export const selectUserInvoicesStatus = (state: StoreType) => state.user.userInvoicesStatus;

export const selectPendingSchoolLicence = (state: StoreType) => state.user.pendingSchoolLicence;

export const selectIsMeLoaded = (state: StoreType): boolean => !!state.user.me;

export const selectUser = createSelector(
	[
		getMe,
		getSubscription,
		getPaymentMethod,
		getExperiments,
		getSubscriptionUpgradePreview,
		getStripeSubscriptionDetails,
	],
	(
		me,
		subscription,
		paymentMethod,
		experiments,
		subscriptionUpgradePreview,
		stripeSubscriptionDetails
	): User | undefined => {
		if (!me) return;

		const subjectsFlattened = flattenUserSubjects(me.subjects);
		// @ts-expect-error (experiments.*.data is a very loosely defined object, i.e. suspension is not part of the definition)
		const pauseExperimentStatus = experiments && experiments['GRO-6']?.data?.suspension?.status;
		// SM-117: withdrawal (Widerruf) request state. For Stripe-backed
		// subscriptions kimuno owns it via the revocation block — an (always-open)
		// `request` means a withdrawal is in flight. Legacy Zuora subs still read the
		// WIDERRUF experiment hack.
		const hasStripeSubscription = isStripeBackedSubscription(stripeSubscriptionDetails);
		const revocationRequest = stripeSubscriptionDetails?.revocation?.request;
		const legacyWithdrawalStatus: WithdrawalRequestStatus | undefined =
			// @ts-expect-error (experiments.*.data is a very loosely defined object, i.e. suspension is not part of the definition)
			experiments && experiments['WIDERRUF']?.data?.suspension?.status;
		const legacyWithdrawalDate: number | undefined =
			// @ts-expect-error (experiments.*.data is a very loosely defined object, i.e. suspension is not part of the definition)
			experiments && experiments['WIDERRUF']?.data?.suspension?.date;
		const withdrawalRequestStatus: WithdrawalRequestStatus | undefined = hasStripeSubscription
			? revocationRequest
				? 'requested'
				: undefined
			: legacyWithdrawalStatus;
		const withdrawalRequestedAt: number | undefined = hasStripeSubscription
			? revocationRequest
				? new Date(revocationRequest.submittedAt).getTime()
				: undefined
			: legacyWithdrawalDate;
		const allowAutomaticCancellation =
			subscription?.cancellation?.canCancel &&
			!['requested', 'granted'].includes(pauseExperimentStatus) &&
			withdrawalRequestStatus !== 'requested';

		const isMUMember =
			!me.mu?.isWalkIn &&
			(me.email?.includes('@meinunterricht.') || me.email?.includes('@klab-berlin.'));

		const result: User = {
			id: me._id,
			email: me.email,
			firstName: me.firstName,
			lastName: me.lastName,
			classes: me.classes,
			schoolType: me.schoolType,
			school: me.school,
			subjects: me.subjects,
			subjectsFlattened,
			groupId: me.groupId,
			isAdmin: me.rights?.admin,
			isCoreMember: isMUMember && !!me.mu?.isCoreMember,
			isMUMember,
			isSubscribedToNewsletter: me.marketing?.mailings?.newsletter?.doubleOptIn === 'confirmed',
			isFree: (subscription?.licence?.priceTotal || 0) <= 0,
			isReadOnlyLicence: !!subscription?.licence?.readonlyMembership,
			isTrainee: me.isTrainee,
			isTrial: me.type === userType.basis ? false : subscription && subscription.licence?.hasTrial,
			// Trial detection covers both billing providers:
			// - Zuora: `subscription.trial.isInTrial` set by the legacy backend.
			// - Stripe (SM-92): the kimuno-backed subscription-details endpoint
			//   reports `status === 'trialing'` on the shadow record. We fold both
			//   into the same flag so all existing gates (canUpgrade, etc.) light
			//   up consistently.
			isInTrial:
				me.type === userType.basis
					? false
					: subscription?.trial?.isInTrial ||
						stripeSubscriptionDetails?.subscription?.status === 'trialing',
			isParent: me.isParent,
			isPremium: premiumUserTypes.includes(me.type),
			hasDocumentLevelAccess: [userType.document, userType.kombi].includes(me.type),
			isBasisUser: me.type === userType.basis,
			isBibliothekUser: me.type === userType.document,
			isCourseUser: me.type === userType.courses,
			isKombiUser: me.type === userType.kombi,
			isAiOnlyUser: me.type === userType.aiOnly,
			isWalkIn: me.mu?.isWalkIn || false,
			hidePinAction: me.mu?.isWalkIn || false,
			hideCollectionAction: me.mu?.isWalkIn || false,
			hideDownloadAction: me.mu?.isWalkIn || false,
			isKeycloakSSO: me.isKeycloakSSO,
			isLuxSSO: me.isLuxSSO,
			isSchoolUser: me.isSchoolUser,
			isMigratedSchoolUser: me.isMigratedSchoolUser,
			isLibraryUser: subscription?.licence?.category === licenceCategory.LIBRARY,
			isStripeUser: !!me.isStripeUser,
			hasStripeHistory: !!me.hasStripeHistory,
			choseToStayBasis: me.choseToStayBasis,
			redirectTo: me.registration && me.registration.redirectTo && me.registration.redirectTo.docId,
			downloads: {
				counter: me.downloads?.counter,
				max: me.downloads?.max,
			},
			address: {
				areaCode: me.address?.areaCode,
				city: me.address?.city,
				country: me.address?.country,
				state: me.address?.state,
				street: me.address?.street,
			},
			gender: me.gender,
			birthday: me.birthday,
			invoiceAddress: {
				gender: me.invoiceAddress?.gender,
				firstName: me.invoiceAddress?.firstName,
				lastName: me.invoiceAddress?.lastName,
				areaCode: me.invoiceAddress?.areaCode,
				city: me.invoiceAddress?.city,
				country: me.invoiceAddress?.country,
				street: me.invoiceAddress?.street,
				state: me.invoiceAddress?.state,
				institution: me.invoiceAddress?.institution,
			},
			licenceId: me.payment?.licencemodelId,
			paymentMethod: paymentMethod,
			onboardingAttempts: me.data?.onboarding?.attempts || 0,
			createdAt: Number(me.created?.timestamp),
			allowAutomaticCancellation,
			pauseExperimentStatus,
			withdrawalRequestStatus,
			withdrawalRequestedAt,
			type: me.type,
			signUpMethod: me.registration?.method,
			didReachCoursesLimit: me.didReachCoursesLimit,
			isSchoolAdmin: me.rights?.schoolAdmin,
			teacherRoles: me.teacherRoles || [],
			federalState: me.federalState,
			schoolStage: me.schoolStage,
			schoolStages: me.schoolStages || [],
			manageableSchools: me?.manageableSchools || [],
			lbMigration: me?.lbMigration,
			isMukiBlocked: me?.isMukiBlocked,
		};

		if (subscriptionUpgradePreview !== undefined || result.isBasisUser || result.isStripeUser) {
			result.canUpgrade = canUpgrade(
				result,
				subscriptionUpgradePreview,
				subscription,
				stripeSubscriptionDetails
			);
		}

		return result;
	}
);

export const selectIsOnboardingCompleted = createSelector([getMe], (me) => {
	if (!me) {
		return true;
	}

	const onboarding = me?.data?.onboarding || {};
	const onboardingAttempts = onboarding.attempts || 0;
	const isOnboardingActive = onboardingAttempts < config.onboarding.maxAttempts;
	const isOnboardingCompleted = onboarding.finished;

	return !isOnboardingActive || isOnboardingCompleted;
});

export const selectDownloadCount = (state: StoreType) =>
	state.user.me && state.user.me.downloads.counter;

export const selectUserInvoiceHistory = createSelector([getInvoiceHistory], (invoiceHistory) => {
	if (!invoiceHistory) return;
	return invoiceHistory;
});

export const selectSchoolInvoiceHistory = createSelector(
	[getSchoolInvoiceHistory],
	(invoiceHistory) => {
		if (!invoiceHistory) return;
		return invoiceHistory;
	}
);

export const selectIsAuthenticated = (state: StoreType) => state.user.isAuthenticated;

export const selectTeachersList = (state: StoreType) => {
	return state.user?.teachersList?.teachers;
};

export const selectSchoolAdmins = (state: StoreType) => {
	return state.user?.schoolAdmins?.admins;
};

export const selectTeachersListSize = (state: StoreType) => {
	return state.user?.teachersList?.size;
};

export const selectSelectedSchoolId = (state: StoreType) => state.user?.selectedSchoolId;

// `isStripeUser` is also true for individual Stripe subscribers, and removing an
// admin who holds a teacher seat keeps `rights.schoolAdmin` — so all three count.
export const selectIsStripeSchoolAdmin = createSelector(
	[selectUser],
	(user) => !!user?.isSchoolAdmin && !!user.isStripeUser && (user.manageableSchools?.length ?? 0) > 0
);

export const selectSelectedSchool = createSelector(
	[selectUser, selectSelectedSchoolId],
	(user, selectedSchoolId) => {
		if (!user?.manageableSchools || !selectedSchoolId) {
			return null;
		}

		if (!selectedSchoolId && user.manageableSchools.length > 0) {
			return user.manageableSchools[0];
		}

		return user.manageableSchools.find((school) => school.id === selectedSchoolId) || null;
	}
);

const canUpgrade = (
	user: User,
	subscriptionUpgradePreview?: SubscriptionPreviewUpgradeResponse,
	subscription?: SubscriptionResponse,
	stripeSubscriptionDetails?: SubscriptionDetailsResponse
): boolean => {
	// For users with Sachen-Anhalt licence we don't show the upgrade button
	const isSAlicence = Number(subscription?.licence?.licenceModelId) === 5670;
	// For users with Schnupperabo licence we don't show the upgrade button
	const isSchnupperabo = Number(subscription?.licence?.licenceModelId) === 2220;
	if (
		user?.isSchoolUser ||
		user?.isInTrial ||
		user?.isLibraryUser ||
		user?.isWalkIn ||
		isSAlicence ||
		isSchnupperabo
	) {
		return false;
	}

	// SM-92 Phase 6: any user with a mapped `payment.stripeCustomerId` sees the
	// upgrade entry — both paid Stripe customers (in-place upgrade) and the
	// rarer "Basis with a customer record" case (e.g. a previous Stripe
	// subscriber whose sub was cancelled; they can re-subscribe via the same
	// flow). The legacy `subscriptionUpgradePreview.allowToUpgrade` is
	// Zuora-specific and never populated for Stripe customers, so without this
	// branch the side-nav button would never appear for them. Top-tier
	// (`fullAccess`) users still see the entry — the `/upgrade-options` page
	// renders the current-plan + school-license blocks with no upgrade cards
	// when there are no options, so an empty list is a valid state.
	//
	// Fail-closed during the load window: `isInTrial` only flips true once
	// `stripeSubscriptionDetails` resolves, but `canUpgrade` would otherwise
	// light up immediately for any `isStripeUser`, briefly exposing the
	// Upgrade entry to a trialing user before the trial check kicks in. So we
	// require the details to be loaded before granting the upgrade entry to
	// Stripe users.
	if (user?.isStripeUser) {
		if (stripeSubscriptionDetails === undefined) {
			return false;
		}

		// SM-189 AC#5: premium users (types 2,3,5) whose Stripe subscription is no
		// longer Active lose the upgrade entry — both the side-nav "Upgraden" button
		// and "Abo anpassen" in the Mein-Konto dialog (both gate on `canUpgrade`).
		// "Active" follows the ticket's definition ("the user didn't cancel"): the
		// status must be `active` AND no cancellation may be scheduled. So cancelled,
		// paused, expired, and scheduled-to-cancel-at-term-end (`isCanceled`) subs all
		// fail the check. Kombi (type 4) is excluded — its upgrade is the school-
		// licence redirect (AC#3), which stays visible regardless of status.
		const stripeSubscription = stripeSubscriptionDetails.subscription;
		const isActiveStripeSubscription =
			stripeSubscription?.status === 'active' && !stripeSubscription.isCanceled;
		if (user.isPremium && !user.isKombiUser && !isActiveStripeSubscription) {
			return false;
		}

		return true;
	}

	// Fresh basis users (no subscription) see the upgrade entry — the click
	// handler routes them to the legacy `/premium` flow. Basis users that
	// still carry a subscription object (e.g. a cancelled sub mid-term) fall
	// through to the preview gate below.
	if (user?.isBasisUser && !subscription) {
		return true;
	}

	// Zuora paid users still gate on the legacy preview.
	return subscriptionUpgradePreview?.allowToUpgrade || false;
};
